import { create } from 'zustand';

/**
 * Home's backdrop on this device: the living scene (follows the atmosphere) or your own photo.
 * The photo is downscaled and kept in this browser only; its brightness picks light or smoked glass.
 */
export type WallpaperChoice = 'scene' | 'photo';

const KEY = 'nw.wallpaper';
const PHOTO_KEY = 'nw.wallpaper.photo';
const LUM_KEY = 'nw.wallpaper.lum';
const read = (k: string) => {
  try {
    return window.localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string | null) => {
  try {
    if (v === null) window.localStorage.removeItem(k);
    else window.localStorage.setItem(k, v);
    return true;
  } catch {
    return false;
  }
};

function averageLuminance(img: CanvasImageSource) {
  const c = document.createElement('canvas');
  c.width = 48;
  c.height = 32;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0, 48, 32);
  const d = ctx.getImageData(0, 0, 48, 32).data;
  let sum = 0;
  for (let i = 0; i < d.length; i += 4) sum += (0.2126 * d[i]! + 0.7152 * d[i + 1]! + 0.0722 * d[i + 2]!) / 255;
  return sum / (d.length / 4);
}

interface WallpaperState {
  choice: WallpaperChoice;
  photo: string | null;
  /** Light or dark glass over the photo. */
  photoMode: 'light' | 'dark';
  choose: (c: WallpaperChoice) => void;
  setPhoto: (file: File) => Promise<boolean>;
}

const storedPhoto = read(PHOTO_KEY);

export const useWallpaper = create<WallpaperState>((set) => ({
  choice: read(KEY) === 'photo' && storedPhoto ? 'photo' : 'scene',
  photo: storedPhoto,
  photoMode: Number(read(LUM_KEY) ?? '0.6') < 0.5 ? 'dark' : 'light',
  choose: (choice) => {
    if (choice === 'photo' && !read(PHOTO_KEY)) return;
    write(KEY, choice);
    set({ choice });
  },
  setPhoto: async (file) => {
    const bitmap = await createImageBitmap(file).catch(() => null);
    if (!bitmap) return false;
    const scale = Math.min(1, 2400 / Math.max(bitmap.width, bitmap.height));
    const c = document.createElement('canvas');
    c.width = Math.round(bitmap.width * scale);
    c.height = Math.round(bitmap.height * scale);
    c.getContext('2d')!.drawImage(bitmap, 0, 0, c.width, c.height);
    let data = c.toDataURL('image/jpeg', 0.85);
    if (!write(PHOTO_KEY, data)) {
      data = c.toDataURL('image/jpeg', 0.6);
      if (!write(PHOTO_KEY, data)) return false;
    }
    const lum = averageLuminance(c);
    write(LUM_KEY, String(lum));
    write(KEY, 'photo');
    set({
      choice: 'photo',
      photo: data,
      photoMode: lum < 0.5 ? 'dark' : 'light',
    });
    return true;
  },
}));
